import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, AlertTriangle, Trash2, RefreshCw, Wifi, WifiOff, Building2, Search, X as XIcon } from 'lucide-react';
import { listAnnouncements, deleteAnnouncements, triggerProcessing } from '../lib/api/index.js';
import type { ProcessingStatus } from '../lib/api/types.js';
import { format, formatDistanceToNow, isToday } from 'date-fns';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { useGlobalArticleGeneration } from '../hooks/useGlobalArticleGeneration.js';
import { ArticleGenerationBadge } from '../components/ArticleGenerationBadge.js';
import { EntityGroupFilter, useEntityGroupPill } from '../components/EntityGroupFilter.js';
import { CoverageBadges } from '../components/CoverageBadges.js';
import { DateBar } from '../components/DateBar.js';

// Provenance = the *temporal* axis (how/when a row entered), distinct from the
// editorial-classification axis carried by the entity-group pills + coverage
// badges. Market-monitor filings and Discovery/triage picks are BOTH "today's
// primary announcements" — they differ only by classification (Majors vs
// Picked, shown as badges), so they share the 'today' tab rather than being
// split here. Only genuinely-derived rows (back-sourced / image-refetch) get
// their own tab.
type ProvenanceTab = 'today' | 'backdated' | 'image_backfill';
const PROVENANCE_TABS: { value: ProvenanceTab; label: string; title: string }[] = [
  { value: 'today',          label: 'Today',          title: "Today's primary announcements — market-monitor filings and Discovery/triage picks (classification shown by the badges)" },
  { value: 'backdated',      label: 'Backdated',      title: 'Older filings back-sourced from a parent announcement' },
  { value: 'image_backfill', label: 'Image Backfill', title: 'Image re-fetch maintenance on older filings' },
];

const statusColors: Record<ProcessingStatus, string> = {
  uploading: 'bg-grey-200 text-grey-600',
  uploaded: 'bg-grey-200 text-grey-600',
  queued: 'bg-blue-100 text-blue-700',
  extracting: 'bg-yellow-100 text-yellow-700',
  enriching: 'bg-orange-100 text-orange-700',
  generating: 'bg-purple-100 text-purple-700',
  completed: 'bg-success-100 text-success-700',
  failed: 'bg-danger-100 text-danger-700',
};

// Known activity_sub_type values from migration 027 + observed in the wild.
// Anything unmapped falls through to a humanised snake_case → Title Case.
const SUB_TYPE_LABELS: Record<string, string> = {
  m_and_a: 'M&A',
  capital_raise: 'Capital Raise',
  listing_ipo: 'Listing/IPO',
  board_change: 'Board Change',
  strategy_change: 'Strategy',
  director_trading: 'Director Trade',
  director_appointment: 'Director Appt',
  substantial_holder_change: 'Substantial Holder',
  buyback: 'Buyback',
  trading_halt: 'Trading Halt',
  suspension: 'Suspension',
  drilling_result: 'Drilling Result',
  exploration_update: 'Exploration',
  resource_update: 'Resource Update',
  quarterly_report: 'Quarterly',
};

function humaniseSubType(s: string): string {
  return SUB_TYPE_LABELS[s] || s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Distinct colours per intelligence_signals.signal_type so an operator can
// scan the list and spot signal-bearing rows by hue alone.
const SIGNAL_LABELS: Record<string, { label: string; color: string }> = {
  insider_trade:     { label: 'Insider Trade',     color: 'bg-purple-100 text-purple-700' },
  regulatory_filing: { label: 'Regulatory Filing', color: 'bg-blue-100 text-blue-700' },
  price_spike:       { label: 'Price Spike',       color: 'bg-orange-100 text-orange-700' },
  trading_halt:      { label: 'Trading Halt',      color: 'bg-red-100 text-red-700' },
};

export function AnnouncementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<ProcessingStatus | ''>(
    (searchParams.get('status') as ProcessingStatus) || ''
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const limit = 200; // Matches Articles — a day's worth fits one page comfortably.
  const queryClient = useQueryClient();
  // Entity-group pill (sticky across all four list pages via localStorage).
  const [entityGroup] = useEntityGroupPill();
  const entityGroupParam = entityGroup === 'all' ? undefined : entityGroup;
  // Provenance tab — clean separate views by how the row entered the system.
  // 'market' (genuine new filings) is the default; the others isolate the
  // Discovery-Engine, parent-triggered back-source, and image-backfill streams.
  const [provenanceTab, setProvenanceTab] = useState<ProvenanceTab>('today');
  // Day strip + unified search — same UX as Articles/Notifications/Social.
  // Date axis is COALESCE(created_at, uploaded_at) Sydney-local (matches the
  // existing "New today" partition's semantic — when the announcement landed
  // in our system, not the ASX-reported event date).
  const [anchorDate, setAnchorDate] = useState<string>(todayYMD());
  const [selectedDate, setSelectedDate] = useState<string>(todayYMD());
  const [searchInput, setSearchInput] = useState(searchParams.get('company') || '');
  const debouncedSearch = useDebounced(searchInput.trim(), 300);
  const searchActive = debouncedSearch.length > 0;
  // Exchange-lane drill-through from the dashboard coverage card. Like search,
  // it spans ALL dates (the card counts are lifetime totals, so filtering to
  // today's selected day would show almost nothing). URL-only, no UI control.
  const exchangeFilter = searchParams.get('exchange') || '';
  const spanAllDates = searchActive || !!exchangeFilter;
  const days = useMemo(() => {
    const out: string[] = [];
    for (let i = 6; i >= 0; i--) out.push(shiftDays(anchorDate, -i));
    return out;
  }, [anchorDate]);
  const resetPage = useCallback(() => setPage(1), []);

  const { connected } = useWebSocket({
    channels: ['assayerai:announcements'],
    onEvent: {
      status_change: () => {
        queryClient.invalidateQueries({ queryKey: ['announcements'] });
      },
    },
    autoInvalidate: true,
    debug: false,
  });

  const { getGenerationState } = useGlobalArticleGeneration();

  const { data, isLoading, error } = useQuery({
    queryKey: searchActive
      ? ['announcements', 'search', debouncedSearch, statusFilter, entityGroup, provenanceTab, exchangeFilter]
      : ['announcements', 'date', selectedDate, statusFilter, page, entityGroup, provenanceTab, exchangeFilter],
    queryFn: () =>
      listAnnouncements({
        status: statusFilter || undefined,
        // Search / exchange drill-through span all dates; otherwise the date
        // filter narrows to the selected day.
        ...(searchActive
          ? { search: debouncedSearch }
          : spanAllDates
            ? {}
            : { date: selectedDate }),
        exchange: exchangeFilter || undefined,
        page: spanAllDates ? 1 : page,
        limit,
        entity_group: entityGroupParam,
        provenance: provenanceTab,
      }),
  });

  // Status / search persist to the URL so a reload restores the operator's
  // active filter; date and entity_group are session-state only (date defaults
  // to today, entity_group lives in localStorage and is the same across tabs).
  const handleStatusChange = (status: ProcessingStatus | '') => {
    setStatusFilter(status);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (status) params.set('status', status);
    else params.delete('status');
    params.set('page', '1');
    setSearchParams(params);
  };
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    // 'company' is the legacy URL key — kept for back-compat on shared links.
    if (value) params.set('company', value);
    else params.delete('company');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Selection is page-scoped (the page only shows ids from data.items),
    // so a checked id from page 1 is invisible — and unselectable — on page 2.
    // Clearing here prevents stale ids leaking into bulk Reprocess/Delete.
    setSelectedIds(new Set());
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const deleteMutation = useMutation({
    mutationFn: deleteAnnouncements,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      setSelectedIds(new Set());
      if (result.deleted > 0) {
        alert(`Deleted ${result.deleted} announcement(s).${result.skipped > 0 ? ` ${result.skipped} skipped (have articles).` : ''}${result.errors ? `\n\nErrors:\n${result.errors.join('\n')}` : ''}`);
      } else {
        alert(`No announcements deleted.${result.errors ? `\nErrors:\n${result.errors.join('\n')}` : ''}`);
      }
    },
    onError: (error: Error) => {
      alert(`Error: ${error.message}`);
    },
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked && data?.items) {
      setSelectedIds(new Set(data.items.map(a => a.announcement_id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    checked ? next.add(id) : next.delete(id);
    setSelectedIds(next);
  };

  const [reprocessing, setReprocessing] = useState(false);

  const handleReprocess = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Reprocess ${selectedIds.size} announcement(s)?\n\nThis will re-extract and regenerate articles.`)) return;
    setReprocessing(true);
    let queued = 0, failed = 0;
    for (const id of selectedIds) {
      try { await triggerProcessing(id, true, false); queued++; } catch { failed++; }
    }
    setReprocessing(false);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: ['announcements'] });
    if (failed > 0) alert(`Queued ${queued}. ${failed} failed.`);
  };

  const handleDelete = () => {
    if (selectedIds.size === 0) return;
    if (confirm(`Delete ${selectedIds.size} announcement(s)?\n\nOnly those without articles will be deleted.`)) {
      deleteMutation.mutate(Array.from(selectedIds));
    }
  };

  const allSelected = data?.items && data.items.length > 0 && data.items.every(a => selectedIds.has(a.announcement_id));
  const someSelected = selectedIds.size > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-grey-900">Announcements</h1>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            connected ? 'bg-success-100 text-success-700' : 'bg-grey-100 text-grey-400'
          }`}>
            {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {connected ? 'Live' : 'Offline'}
          </span>
          {data && <span className="text-sm text-grey-400">{data.total} total</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {someSelected && (
            <>
              <button onClick={handleReprocess} disabled={reprocessing}
                className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                <RefreshCw className={`h-3.5 w-3.5 ${reprocessing ? 'animate-spin' : ''}`} />
                {reprocessing ? 'Queuing...' : `Reprocess (${selectedIds.size})`}
              </button>
              <button onClick={handleDelete} disabled={deleteMutation.isPending}
                className="px-3 py-1.5 bg-danger-600 hover:bg-danger-700 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                <Trash2 className="h-3.5 w-3.5" /> Delete ({selectedIds.size})
              </button>
            </>
          )}
          <Link to="/upload" className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg">
            Upload New
          </Link>
        </div>
      </div>

      {/* Entity-group filter pills — sticky across Articles, Announcements,
          Notifications, Social. */}
      <EntityGroupFilter />

      {/* Provenance tabs — clean separate views by how the row entered the
          system. Same date strip / search / entity-group filter / badges in
          every tab; only the underlying provenance differs. */}
      <div className="flex gap-1 border-b border-grey-200">
        {PROVENANCE_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => { setProvenanceTab(t.value); resetPage(); }}
            title={t.title}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              provenanceTab === t.value
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-grey-600 hover:text-grey-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search — full-width row above the day strip. Mirrors Articles /
          Notifications: typing here spans all dates and statuses, the day
          strip is hidden while search is active. */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by title, ticker, company, or entity…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
          />
          {searchInput && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-700"
              aria-label="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value as ProcessingStatus | '')}
          className="px-3 py-1.5 text-sm border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          <option value="">All Statuses</option>
          <option value="queued">Queued</option>
          <option value="extracting">Extracting</option>
          <option value="enriching">Enriching</option>
          <option value="generating">Generating</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        {searchActive && (
          <span className="text-xs text-grey-500">Searching across all dates</span>
        )}
      </div>

      {/* Day strip — hidden when search is active (search spans all days). */}
      {!searchActive && (
        <DateBar
          days={days}
          selectedDate={selectedDate}
          onSelect={(d) => { setSelectedDate(d); resetPage(); }}
          onShiftWeek={(delta) => setAnchorDate(shiftDays(anchorDate, delta * 7))}
          onToday={() => {
            setAnchorDate(todayYMD());
            setSelectedDate(todayYMD());
            resetPage();
          }}
        />
      )}

      {/* Grouped by Entity */}
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-r-transparent" />
          <p className="mt-3 text-sm text-grey-500">Loading...</p>
        </div>
      ) : error ? (
        <div className="p-12 text-center">
          <AlertTriangle className="h-8 w-8 text-danger-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-grey-900">Error loading announcements</p>
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="p-12 text-center">
          <FileText className="h-8 w-8 mx-auto mb-2 text-grey-300" />
          <p className="text-sm font-semibold text-grey-900">No announcements found</p>
        </div>
      ) : (
        <AnnouncementGroups
          items={data.items}
          selectedIds={selectedIds}
          onSelectOne={handleSelectOne}
          getGenerationState={getGenerationState}
          statusColors={statusColors}
          totalPages={data.pages}
          currentPage={page}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}


// Group announcements by company_code, sorted by most recent first
interface EntityGroup {
  code: string;
  name: string;
  items: any[];
  latestDate: string;
}

// Hoisted out of AnnouncementGroups so the same grouping logic feeds both the
// "Older" section here and any future caller. Pure function — no React deps.
function groupByEntity(items: any[]): EntityGroup[] {
  const map = new Map<string, EntityGroup>();
  for (const a of items) {
    const code = a.company_code || 'UNKNOWN';
    // Prefer the resolved entity name (company_name is just the ticker for
    // Discovery/triage picks). The header hides it when it equals the ticker.
    const entityName = (a.entity_display_name || a.company_name || '').trim();
    if (!map.has(code)) {
      map.set(code, { code, name: entityName || code, items: [], latestDate: a.announcement_date });
    }
    const g = map.get(code)!;
    g.items.push(a);
    if (a.announcement_date > g.latestDate) g.latestDate = a.announcement_date;
    if (entityName && g.name === code) g.name = entityName;
  }
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime(),
  );
}

// Tickers we synthesise for non-ASX entities (research lists, OTC, etc.) —
// these aren't useful as an identifier on their own, so we surface the
// company_name in their place. Mirrors the entity-grouping rule in ArticlesPage.
const SYNTHETIC_TICKERS = new Set(['PRIVATE', 'OTC', 'OTCQX', 'OTCQB', 'PINK', 'PENDING']);

// Direct-input research/brief rows (2026-08-03): recognised so the list shows
// a class chip instead of the PENDING placeholder ticker + metadata text.
const RESEARCH_SOURCE_TYPES = new Set(['research_markdown', 'research_pdf', 'editorial_brief']);
function researchChip(sourceType?: string) {
  if (!sourceType || !RESEARCH_SOURCE_TYPES.has(sourceType)) return null;
  const label = sourceType === 'editorial_brief' ? 'BRIEF' : 'RESEARCH';
  return (
    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-violet-100 text-violet-700 flex-shrink-0">
      {label}
    </span>
  );
}

function AnnouncementRow({ a, selectedIds, onSelectOne, getGenerationState, statusColors, showEntity = false }: {
  a: any; selectedIds: Set<string>; onSelectOne: (id: string, checked: boolean) => void;
  getGenerationState: (id: string) => any; statusColors: Record<string, string>;
  // Set true when the row renders outside an entity-grouped panel (e.g. New
  // today). The grouped panels already label the entity in their header, so
  // adding it per row would be redundant noise there.
  showEntity?: boolean;
}) {
  const gen = getGenerationState(a.announcement_id);
  const code = (a.company_code || '').toUpperCase();
  const isReal = code && !SYNTHETIC_TICKERS.has(code);
  const isResearch = RESEARCH_SOURCE_TYPES.has(a.source_type);
  // Prefer the resolved entity name over company_name — the latter is just the
  // ticker for Discovery/triage picks. Only show a name when it actually adds
  // something beyond the ticker (otherwise the row reads "RDS  RDS").
  const name = (a.entity_display_name || a.company_name || '').trim();
  const showName = !!name && name.toUpperCase() !== code;
  return (
    <Link to={`/announcements/${a.announcement_id}`}
      className="flex items-start sm:items-center gap-3 sm:gap-4 px-3 sm:px-5 py-3 hover:bg-grey-50 transition-colors">
      <input type="checkbox" checked={selectedIds.has(a.announcement_id)}
        onChange={e => { e.stopPropagation(); onSelectOne(a.announcement_id, e.target.checked); }}
        onClick={e => e.stopPropagation()}
        className="h-4 w-4 mt-0.5 sm:mt-0 rounded border-grey-300 text-primary-600 flex-shrink-0" />
      {showEntity && (
        // sm+: fixed-width entity column with three aligned slots: ticker
        // (fixed), name (fills + truncates), badges (right-edge). Right-
        // aligning the badges to the column edge lines them up vertically
        // down the list and keeps every title starting at the same x.
        // Phones get a stacked meta line inside the title block instead.
        <div className="hidden sm:flex flex-shrink-0 items-center gap-2 w-56 min-w-0">
          {isReal ? (
            <>
              <span className="font-bold text-grey-900 text-sm w-12 flex-shrink-0 truncate">{code}</span>
              <span className="text-xs text-grey-500 truncate flex-1 min-w-0">{showName ? name : ''}</span>
            </>
          ) : isResearch ? (
            <span className="flex items-center gap-1.5 flex-1 min-w-0">
              {researchChip(a.source_type)}
              <span className="text-xs text-grey-500 truncate">Research report</span>
            </span>
          ) : (
            <span className="text-sm text-grey-700 truncate flex-1 min-w-0">{name || code || '—'}</span>
          )}
          <div className="flex items-center gap-1 flex-shrink-0">
            <CoverageBadges tiers={a.coverage_tiers} />
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0">
        {showEntity && (
          <div className="flex sm:hidden items-center gap-1.5 flex-wrap mb-0.5">
            {isReal ? (
              <>
                <span className="font-bold text-grey-900 text-xs">{code}</span>
                {showName && <span className="text-xs text-grey-500 truncate max-w-[12rem]">{name}</span>}
              </>
            ) : isResearch ? (
              researchChip(a.source_type)
            ) : (
              <span className="text-xs text-grey-700 truncate max-w-[16rem]">{name || code || '—'}</span>
            )}
            <CoverageBadges tiers={a.coverage_tiers} />
          </div>
        )}
        <p className="text-sm font-medium text-grey-900 line-clamp-2 sm:line-clamp-1">
          {a.announcement_title}
        </p>
        <OutputBadges announcement={a} generationState={gen} />
        <div className="flex sm:hidden items-center gap-2 mt-1">
          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColors[a.processing_status]}`}>
            {a.processing_status}
          </span>
          <span className="text-xs text-grey-400">
            {format(new Date(a.announcement_date), 'd MMM yy')}
          </span>
        </div>
      </div>
      <span className={`hidden sm:inline-flex px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${statusColors[a.processing_status]}`}>
        {a.processing_status}
      </span>
      <span className="hidden sm:block text-xs text-grey-400 flex-shrink-0 w-20 text-right">
        {format(new Date(a.announcement_date), 'd MMM yy')}
      </span>
    </Link>
  );
}

function AnnouncementGroups({ items, selectedIds, onSelectOne, getGenerationState, statusColors, totalPages, currentPage, onPageChange }: {
  items: any[]; selectedIds: Set<string>; onSelectOne: (id: string, checked: boolean) => void;
  getGenerationState: (id: string) => any; statusColors: Record<string, string>;
  totalPages: number; currentPage: number; onPageChange: (p: number) => void;
}) {
  // Partition by created_at (when the announcement landed in our system),
  // not announcement_date (the ASX-reported event date). A Friday filing
  // scraped on Monday is "new today" to the operator even though the event
  // date is Friday — partitioning on announcement_date would silently bury it.
  //
  // The "Today" section only renders on page 1. On page 2+, today's
  // spillover items must fall into the entity-grouped flow, otherwise the
  // page renders blank when ALL of page 2's items happen to be from today
  // (e.g. backfill day with >50 same-day announcements). Pre-fix bug:
  // today items went to the today array but showTodaySection was false on
  // page 2 → nothing rendered.
  const { todayItems, olderGroups } = useMemo(() => {
    const today: any[] = [];
    const older: any[] = [];
    for (const a of items) {
      const ts = a.created_at ?? a.uploaded_at;
      if (currentPage === 1 && ts && isToday(new Date(ts))) {
        today.push(a);
      } else {
        older.push(a);
      }
    }
    return {
      todayItems: today.sort((a, b) =>
        new Date(b.created_at ?? b.uploaded_at).getTime() - new Date(a.created_at ?? a.uploaded_at).getTime(),
      ),
      olderGroups: groupByEntity(older),
    };
  }, [items, currentPage]);

  const showTodaySection = currentPage === 1 && todayItems.length > 0;

  // Collapse-by-default for Older entity groups with >2 items. Single- and
  // two-item groups expand by default — collapsing them is pure noise (the
  // header already shows the count, and one extra row doesn't earn a click).
  // This diverges from ArticlesPage, which starts everything expanded;
  // Announcements is a high-churn triage surface so density matters more.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Reset collapse state to the size-based default whenever the underlying
  // page items change (page navigation, filter change, websocket refetch).
  // User toggles are intentionally page-scoped — they don't survive a refetch.
  useEffect(() => {
    setCollapsed(new Set(olderGroups.filter(g => g.items.length > 2).map(g => g.code)));
  }, [olderGroups]);

  const toggle = useCallback((code: string) => {
    setCollapsed(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  }, []);

  return (
    <>
      <div className="space-y-4">
        {showTodaySection && (
          <div className="bg-surface rounded-xl border border-primary-200 overflow-hidden shadow-sm ring-1 ring-primary-100">
            <div className="bg-primary-50 border-b border-primary-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-bold text-primary-700">New today</span>
              </div>
              <span className="text-xs text-primary-700/80">{todayItems.length} announcement{todayItems.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="divide-y divide-grey-100">
              {todayItems.map(a => (
                <AnnouncementRow key={a.announcement_id} a={a}
                  selectedIds={selectedIds} onSelectOne={onSelectOne}
                  getGenerationState={getGenerationState} statusColors={statusColors}
                  showEntity />
              ))}
            </div>
          </div>
        )}

        {olderGroups.map(g => (
          <div key={g.code} className="bg-surface rounded-xl border border-grey-200 overflow-hidden shadow-sm">
            <button onClick={() => toggle(g.code)}
              className="w-full bg-grey-50 border-b border-grey-200 px-5 py-3 flex items-center justify-between hover:bg-grey-100 transition-colors text-left">
              <div className="flex items-center gap-3">
                <Building2 className="h-4 w-4 text-grey-500" />
                <span className="font-bold text-grey-900">{g.code}</span>
                {g.code !== g.name && <span className="text-sm text-grey-500">{g.name}</span>}
                <CoverageBadges tiers={g.items[0]?.coverage_tiers} />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-grey-500">{g.items.length} announcement{g.items.length !== 1 ? 's' : ''}</span>
                {collapsed.has(g.code) ? <ChevronDown className="h-4 w-4 text-grey-400" /> : <ChevronUp className="h-4 w-4 text-grey-400" />}
              </div>
            </button>

            {!collapsed.has(g.code) && (
              <div className="divide-y divide-grey-100">
                {g.items
                  .sort((a, b) => new Date(b.announcement_date).getTime() - new Date(a.announcement_date).getTime())
                  .map(a => (
                    <AnnouncementRow key={a.announcement_id} a={a}
                      selectedIds={selectedIds} onSelectOne={onSelectOne}
                      getGenerationState={getGenerationState} statusColors={statusColors} />
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-grey-500">Page {currentPage} of {totalPages}</span>
          <div className="flex gap-1">
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
              className="p-1.5 border border-grey-200 rounded hover:bg-surface disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
              className="p-1.5 border border-grey-200 rounded hover:bg-surface disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// "What came out" chips on each row: editorial categorisation, article count,
// and intelligence signal types. Renders nothing when none apply (rather than
// reserving vertical space for an empty row). Provenance is conveyed by the
// page-level tabs (Market / Discovery / Backdated / Image Backfill), not a chip.
function OutputBadges({ announcement: a, generationState: gen }: { announcement: any; generationState: any }) {
  const articleCount = a.article_count ?? 0;
  const signalTypes: string[] = a.signal_types ?? [];
  const isFailed = a.processing_status === 'failed' && !!a.failure_stage;
  const hasContent = !!a.activity_sub_type || articleCount > 0 || signalTypes.length > 0 || !!gen || isFailed;
  if (!hasContent) return null;

  // For failed rows, surface the stage + reason inline. The full reason is
  // often long (stack traces, provider errors) — clamp to ~80 chars and put
  // the rest in `title` so the operator can hover for the full text.
  const reasonShort = a.failure_reason
    ? (a.failure_reason.length > 80 ? a.failure_reason.slice(0, 77) + '…' : a.failure_reason)
    : null;
  const attemptLabel = (a.failure_attempts != null && a.failure_max_attempts != null)
    ? ` (${a.failure_attempts}/${a.failure_max_attempts})`
    : '';

  return (
    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
      {isFailed && (
        <span
          className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-danger-100 text-danger-700 max-w-md whitespace-normal sm:whitespace-nowrap sm:truncate"
          title={a.failure_reason || ''}
        >
          failed @ {a.failure_stage}{attemptLabel}{reasonShort ? `: ${reasonShort}` : ''}
        </span>
      )}
      {a.activity_sub_type && (
        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-grey-100 text-grey-600">
          {humaniseSubType(a.activity_sub_type)}
        </span>
      )}
      {articleCount > 0 && (
        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-sky-100 text-sky-700">
          {articleCount} article{articleCount === 1 ? '' : 's'}
        </span>
      )}
      {signalTypes.map(t => {
        const meta = SIGNAL_LABELS[t] ?? { label: t, color: 'bg-grey-100 text-grey-600' };
        return (
          <span key={t} className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${meta.color}`}>
            {meta.label}
          </span>
        );
      })}
      {gen && <ArticleGenerationBadge generationState={gen} compact />}
    </div>
  );
}

// =============================================================================
// Date strip helpers — the DateBar itself is the shared component
// (components/DateBar.tsx); these date helpers drive this page's week state.
// =============================================================================

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayYMD(): string {
  return ymd(new Date());
}

function shiftDays(date: string, deltaDays: number): string {
  const d = new Date(date + 'T00:00:00');
  d.setDate(d.getDate() + deltaDays);
  return ymd(d);
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
